import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, PlayCircle, Lightbulb, CheckCircle, Clock, Users } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const tableOfContents = [
  { id: 'overview', title: 'Tutorial Overview' },
  { id: 'what-you-learn', title: 'What You\'ll Learn' },
  { id: 'video-tutorial', title: 'Video Tutorial' },
  { id: 'key-steps', title: 'Key Steps Covered' },
  { id: 'next-steps', title: 'Next Steps' },
];

const relatedArticles = [
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
  { title: 'SweetPilot Brain AI (tutorial)', href: '/doc/article/brain-ai-tutorial' },
  { title: 'Login to SweetPilot', href: '/doc/article/login-to-sweetpilot' },
  { title: 'How to navigate the SweetPilot web app', href: '/doc/article/navigate-web-app' },
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
];

const keySteps = [
  {
    step: 1,
    title: 'Creating Your Account',
    description: 'Learn how to sign up for SweetPilot using email or Google authentication.',
    duration: '2 min',
  },
  {
    step: 2,
    title: 'Setting Up Your Profile',
    description: 'Configure your profile settings and preferences for a personalized experience.',
    duration: '1 min',
  },
  {
    step: 3,
    title: 'Exploring the Dashboard',
    description: 'Navigate through the main dashboard and understand each section.',
    duration: '3 min',
  },
  {
    step: 4,
    title: 'Meeting Your AI Employees',
    description: 'Discover the different AI employees and their specialized capabilities.',
    duration: '2 min',
  },
  {
    step: 5,
    title: 'Your First Conversation',
    description: 'Start chatting with an AI employee and learn how to get the best results.',
    duration: '2 min',
  },
  {
    step: 6,
    title: 'Understanding Credits',
    description: 'Learn how the credit system works and track your usage.',
    duration: '1 min',
  },
];

interface TipBoxProps {
  children: React.ReactNode;
  isDark: boolean;
}

function TipBox({ children, isDark }: TipBoxProps) {
  return (
    <div className={`flex gap-3 p-4 rounded-xl my-6 ${
      isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-100'
    }`}>
      <Lightbulb className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
      <div className={`text-sm ${isDark ? 'text-purple-200' : 'text-purple-800'}`}>
        {children}
      </div>
    </div>
  );
}

export default function OnboardingTutorial() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('');
  const [isAtTop, setIsAtTop] = useState(true);

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: 'Onboarding to SweetPilot - Complete Video Tutorial',
    description: 'Step-by-step video tutorial for onboarding to SweetPilot. Learn how to create your account, set up your profile, explore the dashboard, and start using AI employees.',
    thumbnailUrl: 'https://sweetpilot.com/thumbnails/onboarding-tutorial.jpg',
    uploadDate: '2024-01-01',
    duration: 'PT11M',
    publisher: {
      '@type': 'Organization',
      name: 'SweetPilot',
      logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      const firstSection = sections[0];
      if (firstSection && scrollPosition < firstSection.offsetTop) {
        setIsAtTop(true);
        setActiveSection('');
        return;
      }

      setIsAtTop(false);

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Onboarding to SweetPilot - Video Tutorial | SweetPilot Help Center</title>
        <meta name="description" content="Watch our comprehensive onboarding video tutorial for SweetPilot. Learn how to create your account, navigate the dashboard, meet AI employees, and start your first conversation." />
        <meta name="keywords" content="SweetPilot onboarding, SweetPilot tutorial, SweetPilot video guide, AI employee tutorial, SweetPilot getting started, SweetPilot account setup, SweetPilot walkthrough" />
        <meta property="og:title" content="Onboarding to SweetPilot - Complete Video Tutorial" />
        <meta property="og:description" content="Step-by-step video guide to get started with SweetPilot AI employees." />
        <meta property="og:type" content="video.other" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/onboarding-tutorial" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SweetPilot Onboarding Tutorial" />
        <meta name="twitter:description" content="Complete video tutorial for getting started with SweetPilot." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/onboarding-tutorial" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout
        title=""
        subtitle=""
        showBackButton={false}
      >
        <div className="flex gap-16 lg:gap-20">
          {/* Main Content */}
          <div className="flex-1 max-w-2xl">
            {/* Breadcrumbs */}
            <nav className="mb-8">
              <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                <li>
                  <Link
                    to="/docs"
                    className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                  >
                    All Collections
                  </Link>
                </li>
                <li>
                  <ChevronRight className="w-4 h-4" />
                </li>
                <li>
                  <Link
                    to="/doc/collections/getting-started"
                    className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                  >
                    Getting started
                  </Link>
                </li>
                <li>
                  <ChevronRight className="w-4 h-4" />
                </li>
                <li className={isDark ? 'text-white' : 'text-slate-900'}>
                  Onboarding to SweetPilot (tutorial)
                </li>
              </ol>
            </nav>

            {/* Article Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-10"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                }`}>
                  <PlayCircle className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <div>
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full mb-1 ${
                    isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'
                  }`}>
                    Video Tutorial
                  </span>
                  <h1 className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Onboarding to SweetPilot
                  </h1>
                </div>
              </div>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Get started with SweetPilot in minutes with our comprehensive onboarding video tutorial.
              </p>
              <div className={`flex items-center gap-4 mt-4 text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  11 min watch
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  Beginner friendly
                </span>
              </div>
            </motion.div>

            {/* Article Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
            >
              {/* Overview */}
              <h2 id="overview" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Tutorial Overview
              </h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                Welcome to SweetPilot! This comprehensive onboarding tutorial will guide you through everything you need to know to get started with our AI employee platform. Whether you're new to AI assistants or an experienced user, this video will help you make the most of SweetPilot from day one.
              </p>
              <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                In just 11 minutes, you'll learn how to set up your account, navigate the platform, and start working with AI employees that can transform your productivity.
              </p>

              {/* What You'll Learn */}
              <h2 id="what-you-learn" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                What You'll Learn
              </h2>
              <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <li>How to create and configure your SweetPilot account</li>
                <li>Understanding the dashboard layout and navigation</li>
                <li>Meeting different AI employees and their specializations</li>
                <li>Starting your first conversation with an AI employee</li>
                <li>Using power-ups and advanced features</li>
                <li>How the credit system works</li>
              </ul>

              <TipBox isDark={isDark}>
                <strong>Pro Tip:</strong> Watch this video while logged into your SweetPilot account so you can follow along in real-time!
              </TipBox>

              {/* Video Tutorial */}
              <h2 id="video-tutorial" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Video Tutorial
              </h2>

              {/* Video Embed Placeholder */}
              <div className={`aspect-video rounded-2xl overflow-hidden mb-6 ${
                isDark ? 'bg-dark-800 border border-white/10' : 'bg-slate-100 border border-slate-200'
              }`}>
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
                      isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                    }`}>
                      <PlayCircle className={`w-10 h-10 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    </div>
                    <p className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Onboarding to SweetPilot
                    </p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Click to play video tutorial
                    </p>
                  </div>
                </div>
              </div>

              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                Having trouble with the video?{' '}
                <Link
                  to="/contact"
                  className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                >
                  Contact support
                </Link>
              </p>

              {/* Key Steps Covered */}
              <h2 id="key-steps" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Key Steps Covered
              </h2>
              <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                Here's a breakdown of everything covered in the tutorial:
              </p>

              <div className="space-y-4 mb-8">
                {keySteps.map((item) => (
                  <div
                    key={item.step}
                    className={`p-4 rounded-xl border ${
                      isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                      }`}>
                        <span className="text-sm font-bold">{item.step}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {item.title}
                          </h3>
                          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                            {item.duration}
                          </span>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Next Steps */}
              <h2 id="next-steps" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Next Steps
              </h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                After completing this onboarding tutorial, we recommend:
              </p>
              <ol className={`list-decimal pl-6 mb-6 space-y-3 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <li>
                  <strong>Set up Brain AI</strong> - Watch our{' '}
                  <Link
                    to="/doc/article/brain-ai-tutorial"
                    className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                  >
                    Brain AI tutorial
                  </Link>{' '}
                  to personalize your AI employees with your business knowledge.
                </li>
                <li>
                  <strong>Connect integrations</strong> - Link your favorite tools to streamline your workflow.
                </li>
                <li>
                  <strong>Explore AI employees</strong> - Try different employees to find the ones that best fit your needs.
                </li>
              </ol>

              <div className={`p-6 rounded-2xl text-center ${
                isDark ? 'bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-white/10' : 'bg-gradient-to-br from-purple-50 to-cyan-50 border border-slate-200'
              }`}>
                <CheckCircle className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Ready to Get Started?
                </h3>
                <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                  Create your free account and start using SweetPilot AI employees today.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                >
                  Get Started
                </Link>
              </div>

              {/* Divider */}
              <hr className={`my-12 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

              {/* Related Articles */}
              <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Related Articles
              </h2>
              <div className={`rounded-2xl border overflow-hidden ${
                isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
              }`}>
                {relatedArticles.map((article, index) => (
                  <Link
                    key={index}
                    to={article.href}
                    className={`flex items-center justify-between p-4 transition-colors ${
                      index !== relatedArticles.length - 1 ? (isDark ? 'border-b border-white/5' : 'border-b border-slate-100') : ''
                    } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                  >
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {article.title}
                    </span>
                    <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                  </Link>
                ))}
              </div>

              {/* Feedback Section */}
              <div className={`mt-12 p-8 rounded-2xl text-center ${
                isDark ? 'bg-dark-800/50 border border-white/10' : 'bg-slate-50 border border-slate-200'
              }`}>
                <p className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Was this tutorial helpful?
                </p>
                <div className="flex justify-center gap-4">
                  <button className="text-3xl hover:scale-110 transition-transform" title="No">😞</button>
                  <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat">😐</button>
                  <button className="text-3xl hover:scale-110 transition-transform" title="Yes">😃</button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Sidebar - Table of Contents */}
          <aside className="hidden lg:block w-56 shrink-0 ml-auto">
            <div className="sticky top-24 pl-6 border-l border-slate-200 dark:border-white/10">
              {/* Sidebar Title */}
              <h3 className={`text-base mb-4 ${
                isAtTop
                  ? isDark ? 'text-white font-semibold' : 'text-slate-900 font-semibold'
                  : isDark ? 'text-gray-400' : 'text-slate-600'
              }`}>
                Onboarding Tutorial
              </h3>
              <nav className="space-y-2">
                {tableOfContents.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`block w-full text-left py-1.5 text-sm transition-colors ${
                      activeSection === item.id
                        ? isDark
                          ? 'text-white font-medium'
                          : 'text-slate-900 font-medium'
                        : isAtTop && index === 0
                          ? isDark
                            ? 'text-white font-medium'
                            : 'text-slate-900 font-medium'
                          : isDark
                            ? 'text-gray-500 hover:text-gray-300'
                            : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-16"
        >
          <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
            isDark ? 'bg-purple-500/20' : 'bg-purple-100'
          }`}>
            <span className="text-2xl">🎬</span>
          </div>
          <a
            href="mailto:help@sweetpilot.com"
            className={`text-sm transition-colors ${
              isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
            }`}
          >
            help@sweetpilot.com
          </a>
        </motion.div>
      </StaticPageLayout>
    </>
  );
}
